import { useEffect, useState } from "react";
import { Plus, Trash, FloppyDisk, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, inr } from "../../lib/api";
import { GlassCard, Loader, Modal } from "../kit";

const COUNT = { monthly: 12, quarterly: 4, annual: 1, "one-time": 1 };

function CopyDialog({ year, years, from, grades, onClose, onDone }) {
  const [to, setTo] = useState([]);
  const [fromYear, setFromYear] = useState(year);
  const run = async () => {
    try {
      const r = await api.post("/fees/plans/copy", { academic_year: year, from_grade: from, from_year: fromYear, to_grades: to });
      toast.success(`Copied to ${r.data.copied_to.length} class(es) — edit them if needed`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={`Copy ${from}'s structure`} description={`Targets in session ${year} are replaced. You can edit each class afterwards.`} testid="copy-plan-dialog">
      <label className="mb-4 block text-xs text-zinc-400">Copy from session
        <select className="field mt-1" value={fromYear} onChange={(e) => setFromYear(e.target.value)} data-testid="copy-from-year">{years.map((y) => <option key={y}>{y}</option>)}</select></label>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {grades.filter((g) => g !== from || fromYear !== year).map((g) => (
          <label key={g} className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2 text-sm">
            <input type="checkbox" checked={to.includes(g)} onChange={(e) => setTo(e.target.checked ? [...to, g] : to.filter((x) => x !== g))} data-testid={`copy-to-${g.replace(/\s+/g, "-")}`} />{g}
          </label>
        ))}
      </div>
      <button className="btn-primary mt-5 w-full" disabled={!to.length} onClick={run} data-testid="copy-plan-submit"><Copy size={16} /> Copy to {to.length} class(es)</button>
    </Modal>
  );
}

function PlanEditor({ year, plan, types, onSaved, onCopy }) {
  const [items, setItems] = useState(plan.items);
  useEffect(() => { setItems(plan.items); }, [plan]);
  const tmap = Object.fromEntries(types.map((t) => [t.id, t]));
  const used = new Set(items.map((i) => i.fee_type_id));
  const set = (k, field, v) => setItems(items.map((x, i) => (i === k ? { ...x, [field]: v } : x)));
  const total = items.reduce((a, i) => a + (Number(i.amount) || 0) * (COUNT[tmap[i.fee_type_id]?.frequency] || 1), 0);
  const save = async () => {
    try {
      await api.put("/fees/plans", { academic_year: year, grade: plan.grade, items: items.map((i) => ({ ...i, amount: Number(i.amount), due_day: Number(i.due_day) || 10 })) });
      toast.success(`${plan.grade} structure saved`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const add = () => { const t = types.find((x) => x.enabled && !used.has(x.id)); if (t) setItems([...items, { fee_type_id: t.id, amount: "", due_day: 10, due_date: "" }]); };
  return (
    <GlassCard className="p-4 sm:p-6" data-testid="plan-editor">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div><h3 className="font-display text-xl font-semibold text-white" data-testid="plan-editor-grade">{plan.grade}</h3><p className="text-xs text-zinc-400">Session {year} · yearly total <span className="text-blue-300" data-testid="plan-editor-total">{inr(total)}</span></p></div>
        <div className="flex gap-2"><button className="btn-ghost" onClick={onCopy} disabled={!plan.items.length} data-testid="plan-copy-btn"><Copy size={15} /> Copy to other classes</button>
          <button className="btn-primary" onClick={save} data-testid="plan-save-btn"><FloppyDisk size={15} /> Save</button></div>
      </div>
      <div className="space-y-2">
        {items.map((it, k) => {
          const t = tmap[it.fee_type_id];
          const recurring = ["monthly", "quarterly"].includes(t?.frequency);
          return (
            <div key={k} className="grid grid-cols-2 gap-2 rounded-2xl bg-white/[0.03] p-3 sm:grid-cols-[1.6fr_1fr_1fr_auto] sm:items-center" data-testid={`plan-item-${k}`}>
              <select className="field col-span-2 sm:col-span-1" value={it.fee_type_id} onChange={(e) => set(k, "fee_type_id", e.target.value)} data-testid={`plan-item-type-${k}`}>
                {types.filter((x) => x.enabled && (x.id === it.fee_type_id || !used.has(x.id))).map((x) => <option key={x.id} value={x.id}>{x.name} · {x.frequency}{x.mandatory ? "" : " (optional)"}</option>)}
              </select>
              <input type="number" min={1} className="field" placeholder="Amount ₹" value={it.amount} onChange={(e) => set(k, "amount", e.target.value)} data-testid={`plan-item-amount-${k}`} />
              {recurring ? <label className="flex items-center gap-2 text-xs text-zinc-400">Due day<input type="number" min={1} max={28} className="field !w-20" value={it.due_day} onChange={(e) => set(k, "due_day", e.target.value)} data-testid={`plan-item-dueday-${k}`} /></label>
                : <input type="date" className="field" value={it.due_date} onChange={(e) => set(k, "due_date", e.target.value)} title="Due date (blank = start of session)" data-testid={`plan-item-duedate-${k}`} />}
              <button className="btn-ghost !p-2 !text-rose-300 justify-self-end" onClick={() => setItems(items.filter((_, i) => i !== k))} data-testid={`plan-item-remove-${k}`}><Trash size={14} /></button>
            </div>
          );
        })}
      </div>
      <button className="btn-ghost mt-4" onClick={add} data-testid="plan-add-item"><Plus size={15} /> Add fee</button>
    </GlassCard>
  );
}

export function ClassPlans({ defaultYear }) {
  const [year, setYear] = useState(defaultYear);
  const { data, loading, reload } = useApi(`/fees/plans?academic_year=${encodeURIComponent(year)}`);
  const { data: tdata } = useApi("/fees/types");
  const [sel, setSel] = useState("");
  const [copying, setCopying] = useState(false);
  const next = `${+year.slice(0, 4) + 1}-${String(+year.slice(0, 4) + 2).slice(2)}`;
  const years = [...new Set([...(data?.years || []), year, next])].sort();
  if (loading || !data || !tdata) return <Loader />;
  const plan = data.grades.find((g) => g.grade === sel) || data.grades[0];
  return (
    <div className="grid gap-6 lg:grid-cols-[280px_1fr]" data-testid="class-plans">
      <GlassCard className="p-4">
        <label className="mb-4 block text-xs text-zinc-400">Academic session
          <select className="field mt-1" value={year} onChange={(e) => setYear(e.target.value)} data-testid="plan-year-select">{years.map((y) => <option key={y}>{y}</option>)}</select></label>
        <div className="space-y-1">
          {data.grades.map((g) => (
            <button key={g.grade} onClick={() => setSel(g.grade)} data-testid={`plan-grade-${g.grade.replace(/\s+/g, "-")}`}
              className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition-colors duration-200 ${plan?.grade === g.grade ? "bg-blue-500/20 text-white" : "text-zinc-300 hover:bg-white/5"}`}>
              <span>{g.grade}</span><span className="text-xs text-zinc-500">{g.items.length ? inr(g.annual_total) : "Not set"}</span>
            </button>
          ))}
        </div>
      </GlassCard>
      {plan && <PlanEditor year={year} plan={plan} types={tdata.types} onSaved={reload} onCopy={() => setCopying(true)} />}
      {copying && <CopyDialog year={year} years={years} from={plan.grade} grades={data.grades.map((g) => g.grade)} onClose={() => setCopying(false)} onDone={() => { setCopying(false); reload(); }} />}
    </div>
  );
}
